import {
    BRIDGE_ERROR,
    BRIDGE_LIMITS,
    BRIDGE_PROTOCOL,
    BRIDGE_PROTOCOL_VERSION,
    REQUEST_ID_PATTERN,
} from '../../constants/bridge';
import { fail, isKnownErrorCode, ok } from '../result';
import { isAllowedMethod, isPlainObject, validateArgs } from '../schema';

const REQUEST_KEYS = ['protocol', 'version', 'kind', 'id', 'method', 'payload'];
const RESPONSE_OK_KEYS = ['protocol', 'version', 'kind', 'id', 'ok', 'value'];
const RESPONSE_ERROR_KEYS = ['protocol', 'version', 'kind', 'id', 'ok', 'error'];
const CANCEL_KEYS = ['protocol', 'version', 'kind', 'id'];

function hasExactKeys(candidate, keys) {
    const actual = Object.keys(candidate);
    return actual.length === keys.length && keys.every((key) => Object.hasOwn(candidate, key));
}

function isEnvelope(data) {
    return isPlainObject(data)
        && data.protocol === BRIDGE_PROTOCOL
        && data.version === BRIDGE_PROTOCOL_VERSION;
}

export function isValidRequestId(id) {
    return typeof id === 'string' && REQUEST_ID_PATTERN.test(id);
}

// An origin is accepted only as the canonical "scheme://host[:port]" string of
// an http(s) URL. Wildcards, "null" (sandboxed/file frames) and paths are
// rejected so a configuration typo cannot widen the trust boundary.
export function normalizeOrigin(candidate) {
    if (typeof candidate !== 'string' || !candidate || candidate === '*' || candidate === 'null') return null;
    try {
        const parsed = new URL(candidate);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
        return parsed.origin === candidate ? candidate : null;
    } catch {
        return null;
    }
}

export function parseOriginList(raw) {
    if (typeof raw !== 'string') return [];
    return raw.split(',').map((item) => normalizeOrigin(item.trim())).filter(Boolean);
}

export function isAllowedOrigin(origin, allowlist) {
    const normalized = normalizeOrigin(origin);
    return !!normalized && Array.isArray(allowlist) && allowlist.includes(normalized);
}

export function buildRequest(id, method, payload) {
    return { protocol: BRIDGE_PROTOCOL, version: BRIDGE_PROTOCOL_VERSION, kind: 'request', id, method, payload };
}

export function buildCancel(id) {
    return { protocol: BRIDGE_PROTOCOL, version: BRIDGE_PROTOCOL_VERSION, kind: 'cancel', id };
}

export function buildResponse(id, result) {
    const base = { protocol: BRIDGE_PROTOCOL, version: BRIDGE_PROTOCOL_VERSION, kind: 'response', id };
    if (result.ok) return { ...base, ok: true, value: result.value };
    return { ...base, ok: false, error: { code: result.error.code, message: result.error.message } };
}

export function messageKind(data) {
    return isEnvelope(data) && typeof data.kind === 'string' ? data.kind : null;
}

export function parseRequest(data) {
    if (!isEnvelope(data) || data.kind !== 'request' || !hasExactKeys(data, REQUEST_KEYS)) {
        return { valid: false, id: null, code: BRIDGE_ERROR.INVALID_ARGUMENT };
    }
    if (!isValidRequestId(data.id)) return { valid: false, id: null, code: BRIDGE_ERROR.INVALID_ARGUMENT };
    if (!isAllowedMethod(data.method)) return { valid: false, id: data.id, code: BRIDGE_ERROR.NOT_ALLOWED };
    const checked = validateArgs(data.method, data.payload);
    if (!checked.valid) return { valid: false, id: data.id, code: BRIDGE_ERROR.INVALID_ARGUMENT };
    return { valid: true, id: data.id, method: data.method, payload: checked.value };
}

export function parseCancel(data) {
    if (!isEnvelope(data) || data.kind !== 'cancel' || !hasExactKeys(data, CANCEL_KEYS)) return null;
    return isValidRequestId(data.id) ? data.id : null;
}

export function parseResponse(data) {
    if (!isEnvelope(data) || data.kind !== 'response' || !isValidRequestId(data.id)) return null;
    if (data.ok === true && hasExactKeys(data, RESPONSE_OK_KEYS)) {
        return { id: data.id, result: ok(data.value) };
    }
    if (data.ok === false && hasExactKeys(data, RESPONSE_ERROR_KEYS) && isPlainObject(data.error)) {
        const { code, message } = data.error;
        const safeCode = isKnownErrorCode(code) ? code : BRIDGE_ERROR.FAILED;
        const safeMessage = typeof message === 'string' ? message.slice(0, BRIDGE_LIMITS.MESSAGE_MAX) : undefined;
        return { id: data.id, result: fail(safeCode, safeMessage) };
    }
    return null;
}
